<template>
  <div v-rect-loading="loading" class="microsite-editor">
    <div class="microsite-editor-header">
      <div class="microsite-editor-title">{{ titleLabel }}</div>
    </div>
    <el-tabs :value="activeTab" :before-leave="handleBeforeTabLeave" @tab-click="handleTabClick">
      <el-tab-pane
        class="form-container"
        :name="PAGE_TAB.NAME"
        :label="$t('admin.crm.label.microsite.micrositeName')"
      >
        <WrappedFrom
          ref="formEditor"
          labelWidth="150px"
          :model="microsite"
          :formLines="formLines"
        >
        </WrappedFrom>
        <div class="bottom-btn-group">
          <el-button class="cancel-btn" @click="handleCancel">{{ $t('common.btn.cancel') }}</el-button>
          <el-button v-if="isEditMode" class="save-btn" @click="handleSave">{{ $t('common.btn.save') }}</el-button>
          <el-button v-else class="save-btn" @click="handleSave">{{ $t('admin.crm.btn.microsite.saveAndEdit') }}</el-button>
        </div>
      </el-tab-pane>
      <el-tab-pane
        v-if="isEditMode"
        :name="PAGE_TAB.SET"
        :label="$t('admin.crm.btn.microsite.setMicrosite')"
      >
        <TableList
          no-head
          no-pagination
          :listData="setList"
          :tableColumns="tableColumns"
          :actionBtn="actionBtn"
        >
        </TableList>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import _ from 'lodash'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import WrappedFrom from '@/components/formEditor/wrappedForm.vue'
import { mapActions } from 'vuex'
import { MICROSITE_PATH } from '@/constant/postApplicant'

const PAGE_TAB = {
  NAME: 'name',
  SET: 'set'
}

export default {
  components: {
    TableList,
    WrappedFrom
  },
  data () {
    return {
      PAGE_TAB,
      microsite: {},
      loading: false,
      i18nPrefix: 'admin.company.postApplicant',
      setList: [
        { id: 'webCnLink' },
        { id: 'webEnLink' },
        { id: 'webTcLink' },
        { id: 'mobileCnLink' },
        { id: 'mobileEnLink' },
        { id: 'mobileTcLink' }
      ]
    }
  },
  computed: {
    isEditMode () {
      return this.$route.meta.isEditMode
    },
    actionBtn () {
      return {
        edit: {
          labelI18nKey: 'admin.crm.btn.microsite.setMicrosite',
          onclick: this.handleEdit
        },
        preview: {
          labelI18nKey: 'admin.crm.btn.microsite.preview',
          onclick: this.handlePreview
        }
      }
    },
    tableColumns () {
      return [
        {
          dataKey: 'id',
          labelI18nKey: `${this.i18nPrefix}.microsite.pcOrMobile`,
          formatter: (id) => this.$t(`${this.i18nPrefix}.microsite.${id}`)
        }
      ]
    },
    formLines () {
      const lines = {
        name: {
          required: true,
          label: this.$t(`${this.i18nPrefix}.microsite.micrositeName`),
          placeholder: this.$t('admin.crm.placeholder.microsite.micrositeName'),
          rules: [
            { required: true, message: this.$t('admin.crm.message.microsite.micrositeName') }
          ]
        }
      }
      return lines
    },
    titleLabel () {
      return this.$t(`${this.i18nPrefix}.title.microSite.${this.isEditMode ? 'edit' : 'add'}`)
    },
    activeTab () {
      const tab = this.$route.query.tab
      if (!Object.values(PAGE_TAB).includes(tab)) {
        return PAGE_TAB.NAME
      }
      return tab
    },
    form () {
      return this.$refs.formEditor
    }
  },
  beforeCreate () {
    const tab = this.$route.query.tab
    if (!_.includes(PAGE_TAB, tab)) {
      this.$router.replace({ query: { ...this.$route.query, tab: PAGE_TAB.NAME } })
    }
  },
  created () {
    if (this.isEditMode) {
      this.getMicrosite()
    }
  },
  methods: {
    ...mapActions('postApplicantManagement', ['getMicroSiteById', 'createMicroSite', 'updateMicroSite']),
    async getMicrosite () {
      this.loading = true
      try {
        const micrositeId = this.$route.params.microSiteId
        const resp = await this.getMicroSiteById(micrositeId)
        this.renderMicorsite(resp)
      } finally {
        this.loading = false
      }
    },
    renderMicorsite (resp) {
      if (resp) {
        this.microsite = {
          id: resp.id,
          name: resp.name,
          short_unique_id: resp.url_code,
          webEnLink: `${MICROSITE_PATH.EDIT}${resp.url_code}/pc/en/postApplicant/`,
          webCnLink: `${MICROSITE_PATH.EDIT}${resp.url_code}/pc/zh-cn/postApplicant/`,
          webTcLink: `${MICROSITE_PATH.EDIT}${resp.url_code}/pc/tc/postApplicant/`,
          mobileEnLink: `${MICROSITE_PATH.EDIT}${resp.url_code}/mobile/en/postApplicant/`,
          mobileCnLink: `${MICROSITE_PATH.EDIT}${resp.url_code}/mobile/zh-cn/postApplicant/`,
          mobileTcLink: `${MICROSITE_PATH.EDIT}${resp.url_code}/mobile/tc/postApplicant/`,
          indexUrl: `/v-admin/post-applicant-management/micro-site/detail/${resp.url_code}`
        }
      }
    },
    handleTabClick (pane) {
      const tabName = pane.paneName
      if (tabName === this.activeTab) {
        return
      }
      if (this.handleBeforeTabLeave()) {
        this.$router.push({ query: { ...this.$route.query, tab: tabName } })
      }
    },
    handleBeforeTabLeave () {
      if (this.activeTab !== PAGE_TAB.NAME) {
        return true
      }
      let result = true
      this.form.$refs.form.validate((isSuccess) => {
        result = isSuccess
      })
      return result
    },
    handleEdit (rowData) {
      window.open(this.microsite[rowData.id])
    },
    handlePreview () {
      window.open(this.microsite.indexUrl)
    },
    handleCancel () {
      this.$router.push('/v-admin/post-applicant-management/micro-site/list')
    },
    async handleSave () {
      const result = await this.form.submit()
      if (!result) {
        return
      }

      const micrositeDetail = {
        name: this.microsite.name
      }

      let saveFunction = null
      if (this.isEditMode) {
        micrositeDetail.id = this.microsite.id
        saveFunction = this.updateMicroSite
      } else {
        saveFunction = this.createMicroSite
      }
      this.loading = true
      try {
        const resp = await saveFunction(micrositeDetail)
        if (resp) {
          if (this.isEditMode) {
            this.$router.push('/v-admin/post-applicant-management/micro-site/list')
          } else {
            this.$router.push({
              path: `/v-admin/post-applicant-management/micro-site/edit/${resp.id}`,
              query: {
                tab: PAGE_TAB.SET
              }
            })
            this.renderMicorsite(resp)
          }
        } else {
          this.$message({ message: this.$t('admin.crm.message.microsite.requestError'), type: 'error' })
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.microsite-editor {
  .microsite-editor-header {
    display: flex;
    border-bottom: 1px solid #e4e7ed;
    margin-bottom: 16px;

    .microsite-editor-title {
      padding-top: 4px;
      padding-bottom: 18px;
      line-height: 25px;
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }
  }

  .form-container {
    width: 606px;
    margin: 30px auto 0;

    .bottom-btn-group {
      display: flex;
      justify-content: flex-end;

      .save-btn {
        margin-left: 24px;
        margin-right: 0;
        background: #0096cc;
        border-color: #0096cc;
        color: #ffffff;
      }
    }
  }

  .el-button {
    padding: 8px 28px;
    border-radius: 2px;
    line-height: 22px;
    font-size: 16px;
    font-weight: 500;
    color: #0096cc;
  }
}
</style>
